/**
 * Parse Mad Libs sentence templates into renderable segments.
 * Template syntax: "Your pet in ${style-1} glory with a ${mood-1} soul"
 */

import type { DisplaySentence } from '@/types';

export type Segment =
  | { readonly type: 'text'; readonly value: string }
  | { readonly type: 'slot'; readonly slotId: string }
  | { readonly type: 'upload'; readonly originalText: string };

const SLOT_PATTERN = /\$\{([^}]+)\}/g;

/**
 * Parse a sentence template string into an array of text and slot segments.
 * @param template - The template string with ${slotId} markers
 * @returns Array of segments in order
 */
export function parseSentence(template: string): Segment[] {
  if (!template) return [];

  const segments: Segment[] = [];
  let lastIndex = 0;

  for (const match of template.matchAll(SLOT_PATTERN)) {
    const matchIndex = match.index;
    const slotId = match[1] ?? '';

    // Add text before this slot (if any)
    if (matchIndex > lastIndex) {
      segments.push({ type: 'text', value: template.slice(lastIndex, matchIndex) });
    }

    if (slotId) {
      segments.push({ type: 'slot', slotId });
    }
    lastIndex = matchIndex + match[0].length;
  }

  // Add remaining text after last slot (if any)
  if (lastIndex < template.length) {
    segments.push({ type: 'text', value: template.slice(lastIndex) });
  }

  return segments;
}

/**
 * Parse a sentence for display, converting "Image 2" references in text
 * segments into upload segments. The prompt generation path (parseSentence/fillSentence)
 * remains unchanged — this only affects UI rendering.
 *
 * Pattern matches: "from Image 2" or just "Image 2" (case-insensitive)
 * Same pattern as stripImage2Reference in src/services/fal.ts
 */
export function parseSentenceForDisplay(template: string): Segment[] {
  const baseSegments = parseSentence(template);
  const result: Segment[] = [];

  const IMAGE2_PATTERN = /(\s*from\s+)?Image\s+2\b/gi;

  for (const segment of baseSegments) {
    if (segment.type !== 'text') {
      result.push(segment);
      continue;
    }

    // Split text segments around "Image 2" references
    let lastIdx = 0;
    for (const match of segment.value.matchAll(IMAGE2_PATTERN)) {
      const matchIndex = match.index;

      // Add text before this match
      if (matchIndex > lastIdx) {
        result.push({ type: 'text', value: segment.value.slice(lastIdx, matchIndex) });
      }

      result.push({ type: 'upload', originalText: match[0] });
      lastIdx = matchIndex + match[0].length;
    }

    // Add remaining text after last match
    if (lastIdx < segment.value.length) {
      result.push({ type: 'text', value: segment.value.slice(lastIdx) });
    } else if (lastIdx === 0) {
      // No matches found, keep original segment
      result.push(segment);
    }
  }

  return result;
}

/**
 * Fill a display sentence by replacing ${slotId} markers with actual values.
 * Unfilled optional slots produce empty strings (collapsed spaces).
 * @param sentence - The display sentence with slot definitions
 * @param values - Map of slotId → value (e.g., 'style-1' → 'Cross-Hatching')
 * @returns Filled sentence string with collapsed whitespace
 */
export function fillSentence(
  sentence: DisplaySentence,
  values: ReadonlyMap<string, string>
): string {
  const segments = parseSentence(sentence.sentence);

  const filled = segments
    .map((seg) => {
      if (seg.type === 'text') return seg.value;
      if (seg.type === 'slot') return values.get(seg.slotId) ?? '';
      return '';
    })
    .join('');

  // Collapse multiple spaces and trim
  return filled.replace(/\s{2,}/g, ' ').trim();
}
